import * as THREE from "three";

/** The marker's look: its size against the pet's scale (world units), gold, its rest and peak strength, the pulse. */
export const PET_MARKER = {
  /** Diameter at scale 1 (the pup is ~0.27 long at life size: the ring sits just round its paws). */
  size: 0.36,
  /** The game's gold (ui tokens --gold-hi). */
  colour: 0xffd75c,
  /** Opacity at rest, its slow breath (share and Hz), and at a bark's peak; the peak fades in `pulseSeconds`. */
  rest: 0.5, breath: 0.15, breathHz: 0.6, peak: 0.85, pulseSeconds: 0.5,
  /** A bark's ring grows this much and settles back (outward: the flare never washes over the pet itself). */
  pulseGrow: 0.3,
  /** Off the ground (no z-fight; above the contact shadow). */
  lift: 0.009,
} as const;

/** A soft gold ring, all but clear inside (the pet's paws stand in it unlit), a canvas made once and shared. */
let ringTex: THREE.CanvasTexture | null = null;
function ringTexture(): THREE.CanvasTexture {
  if (ringTex) return ringTex;
  const n = 64, cv = document.createElement("canvas");
  cv.width = cv.height = n;
  const g = cv.getContext("2d")!, c = n / 2;
  const grad = g.createRadialGradient(c, c, 0, c, c, c);
  grad.addColorStop(0, "rgba(255,255,255,0)");
  grad.addColorStop(0.5, "rgba(255,255,255,0.08)");
  grad.addColorStop(0.74, "rgba(255,255,255,1)");
  grad.addColorStop(0.86, "rgba(255,255,255,0.55)");
  grad.addColorStop(1, "rgba(255,255,255,0)");
  g.fillStyle = grad; g.fillRect(0, 0, n, n);
  ringTex = new THREE.CanvasTexture(cv);
  ringTex.colorSpace = THREE.SRGBColorSpace;
  return ringTex;
}

/**
 * (B) The pet's ground marker in battle (petPresent.ts): a soft gold ring round its paws, like a hero's, breathing a
 * little, that flares and grows for a moment on each bark. One plane, additive, no depth write; presentation only.
 */
export class PetMarker {
  readonly mesh: THREE.Mesh;
  private readonly mat: THREE.MeshBasicMaterial;
  private pulse = 0;
  private time = 0;

  constructor(private readonly scale: number) {
    this.mat = new THREE.MeshBasicMaterial({ map: ringTexture(), color: PET_MARKER.colour, transparent: true, opacity: PET_MARKER.rest, depthWrite: false, blending: THREE.AdditiveBlending, toneMapped: false });
    this.mesh = new THREE.Mesh(new THREE.PlaneGeometry(1, 1).rotateX(-Math.PI / 2), this.mat);
    this.mesh.renderOrder = 2;
  }

  /** Its colour (the gold by default; a Crystal Ward shield's blue). */
  tint(colour: number): void { if (this.mat.color.getHex() !== colour) this.mat.color.setHex(colour); }

  /** A bark: the ring flares. */
  flare(): void { this.pulse = 1; }

  /** Under the pet at (x, ground, z), this frame. */
  update(dt: number, x: number, ground: number, z: number): void {
    this.time += dt;
    this.pulse = Math.max(0, this.pulse - dt / PET_MARKER.pulseSeconds);
    const p = this.pulse * this.pulse;
    const breath = 1 + PET_MARKER.breath * Math.sin(this.time * Math.PI * 2 * PET_MARKER.breathHz);
    this.mat.opacity = PET_MARKER.rest * breath + (PET_MARKER.peak - PET_MARKER.rest) * p;
    const s = PET_MARKER.size * this.scale * (1 + PET_MARKER.pulseGrow * p);
    this.mesh.scale.set(s, 1, s);
    this.mesh.position.set(x, ground + PET_MARKER.lift, z);
  }

  set visible(v: boolean) { this.mesh.visible = v; }

  dispose(): void { this.mesh.geometry.dispose(); this.mat.dispose(); this.mesh.removeFromParent(); }
}
